import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import {
  AdminPageHeader,
  ButtonType,
  Card,
  HeaderStyle,
  IconButtonProps,
  IconStyle,
  Theme,
  TableContainer,
} from '../../../../ui/components';
import { useAdminToast } from '../../../../ui/components/AdminToast/AdminToast';
import { LoggerService, dateTimeToCustomFormat } from '../../../../ui/utils';
import LeaderService from '../../../../sdk/services/leaderService';
import RewardService from '../../../../sdk/services/rewardService';
import AuditLogService from '../../../../sdk/services/auditLogService';
import { DynamicObjectInterface } from '../../../../sdk/type';
import { useAPICall } from '../../../../hooks';
import { getRewardListConfig, getCoachListConfig, LeaderData, CoachData, MasterData } from '../leaderConfig';
import AuditHistoryAccordion from '../../audit-log/auditLogAccordion';
import styles from '../index.module.scss';
import { PointAllocationModal, RewardGiftModal } from './editModal';

type SectionState = {
  // eslint-disable-next-line no-unused-vars
  [key in (typeof Sections)[keyof typeof Sections]]: boolean;
};

const Sections = {
  // LeaderDetails: 'leaderDetails',
  PointAllocation: 'pointAllocation',
  RewardGift: 'rewardGift',
} as const;

interface SearchParams {
  campaignId: string;
  recId: string;
}

export const AdminGeneralLeaderDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const apiCall = useAPICall();
  const { adminToast } = useAdminToast();

  const [data, setData] = useState<LeaderData | null>(null);
  const [masterData, setMasterData] = useState<MasterData | null>(null);
  const [showEdit, setShowEdit] = useState<SectionState>(
    Object.fromEntries(Object.values(Sections).map(section => [section, false])) as SectionState,
  );
  const [searchParams, setSearchParams] = useState<SearchParams>();
  const [auditHistoryLog, setAuditHistoryLog] = useState([]);
  const [displayAuditEmpty, setAuditDisplayEmpty] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let keys: SearchParams = {
      campaignId: params.get('campaignId') ?? '',
      recId: params.get('') ?? '',
    };
    setSearchParams(keys);
  }, [location]);

  useEffect(() => {
    if (searchParams) {
      fetchLeaderData();
      fetchMasterData();
    }
  }, [searchParams]);

  const fetchLeaderData = async () => {
    let res = await apiCall(LeaderService.getLeaderDetailsByRecId, `${searchParams?.recId}`);
    let leaderData = res?.data?.data;
    setData(leaderData);
  };

  const fetchMasterData = async () => {
    let list = {
      reward: {
        endpoint: RewardService.getRewardList,
      },
    };
    await Promise.all(Object.values(list).map(l => apiCall(l.endpoint)))
      .then(results => {
        setMasterData(
          Object.keys(list).reduce((result: DynamicObjectInterface, key, idx) => {
            result[key] = results[idx]?.data?.data;
            return result;
          }, {}),
        );
      })
      .catch(error => LoggerService.log('Failed to retrieve master data', error));
  };

  const onBackButtonClick = () => {
    navigate(
      searchParams?.campaignId
        ? `/admin/manage-leader/list?campaignId=${searchParams?.campaignId}`
        : `/admin/manage-leader/list`,
    );
  };

  const toggleEditModal = (section: (typeof Sections)[keyof typeof Sections]) => {
    setShowEdit(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const onSaveLeaderDetails = async (formData: LeaderData) => {
    const activeSection = Object.entries(showEdit).find(entry => entry[1] === true)?.[0] as
      | (typeof Sections)[keyof typeof Sections]
      | undefined;

    const updatedFormData = {
      ...formData,
      section: activeSection,
      rewards: formData?.rewards
        ?.map((reward: DynamicObjectInterface) => {
          reward.rewardRecId = reward.rewardRecId?.toString()?.includes('NEW') ? null : reward.rewardRecId;
          return { ...reward };
        })
        .filter(reward => !(!reward.rewardRecId && reward.rewardStatus === 'REMOVED')),
    };
    const result = await apiCall(LeaderService.updateTeamCoachDetails, formData.recId?.toString(), updatedFormData);

    if (result.status === 200) {
      adminToast({
        message: t('admin.participant.updateSuccess'),
        theme: Theme.SUCCESS,
      });
      await fetchLeaderData();
      toggleEditModal(activeSection as (typeof Sections)[keyof typeof Sections]);
    } else {
      adminToast({
        message: t('admin.participant.updateFail'),
        theme: Theme.ERROR,
      });
    }
  };

  const getButtonConfig = useMemo(() => {
    let commonConfig = {
      iconName: 'pencil' as IconName,
      iconStyle: IconStyle.SOLID,
      theme: Theme.WHITE,
      title: t('admin.participant.edit'),
    };
    return {
      // leaderDetails: [{ ...commonConfig, onClick: () => toggleEditModal(Sections.LeaderDetails) }],
      pointAllocation: [{ ...commonConfig, onClick: () => toggleEditModal(Sections.PointAllocation) }],
      rewardGift: [{ ...commonConfig, onClick: () => toggleEditModal(Sections.RewardGift) }],
    } as { [index: string]: IconButtonProps[] };
  }, []);

  const onEdit = (data: CoachData) => {
    navigate(`/admin/manage-coach/edit?=${data?.recId}&campaignId=${searchParams?.campaignId}`);
  };

  const showModal = (
    <>
      {showEdit.pointAllocation && (
        <PointAllocationModal
          data={data}
          onSave={onSaveLeaderDetails}
          onCancel={() => toggleEditModal(Sections.PointAllocation)}
        />
      )}
      {showEdit.rewardGift && (
        <RewardGiftModal
          data={data}
          masterData={masterData}
          onSave={onSaveLeaderDetails}
          onCancel={() => toggleEditModal(Sections.RewardGift)}
        />
      )}
    </>
  );

  useEffect(() => {
    const fetchAuditLogs = async () => {
      try {
        if (searchParams?.campaignId && data?.recId) {
          const payload = {
            campaignId: searchParams?.campaignId,
            leaderRecId: data?.recId,
          };

          const historyLogResponse = await apiCall(AuditLogService.fetchLog, payload);
          const historyLogResult = historyLogResponse.data;

          setAuditHistoryLog(historyLogResult.data || []);
          setAuditDisplayEmpty(historyLogResult.data.length === 0);
        }
      } catch (error) {
        LoggerService.log('Failed to retrieve audit history log', error);
      }
    };

    fetchAuditLogs();
  }, [searchParams?.campaignId, data?.recId]);

  //const calculateTotal = (valueA: string = '0', valueB: string = '0') => {
  //  return (parseFloat(valueA) + parseFloat(valueB)).toFixed(1);
  //};

  return (
    <div className={styles['leader-details']}>
      <AdminPageHeader withBackButton onBackButtonClick={onBackButtonClick} title={`Leader`} />
      <div className={styles['leader-details-spacing-v']} style={{ marginTop: '0.5rem' }}>
        <Card
          title={`Details`}
          headerStyle={HeaderStyle.HEADER_5}
          buttonType={ButtonType.ICON}
          // button={getButtonConfig.leaderDetails}
        >
          <div className={styles['details']}>
            <div className={`${styles['details-info']} ${styles['details-info-direct-leader']}`}>
              <div className={styles['details-info-item']}>
                <div className={styles['label']}>Campaign ID</div>
                <p>{data?.campaignId ?? '-'}</p>
              </div>
              <div className={styles['details-info-item']}>
                <div className={styles['label']}>Campaign Name</div>
                <p>{data?.campaignName ?? '-'}</p>
              </div>
              <div className={styles['details-info-item']}>
                <div className={styles['label']}>Campaign Period</div>
                <p>
                  {data?.campaignStart && data.campaignEnd
                    ? dateTimeToCustomFormat(data?.campaignStart?.toString() ?? '', 'MMM YYYY') +
                      ' - ' +
                      dateTimeToCustomFormat(data?.campaignEnd?.toString() ?? '', 'MMM YYYY')
                    : '-'}
                </p>
              </div>
            </div>
            <div className={styles['details-info']}>
              <div className={styles['details-info-item']}>
                <div className={styles['label']}>ABO ID</div>
                <p>{data?.aboId ?? '-'}</p>
              </div>
              <div className={styles['details-info-item']}>
                <div className={styles['label']}>Name</div>
                <p>{data?.name ?? '-'}</p>
              </div>
              <div className={styles['details-info-item']}>
                <div className={styles['label']}>Enrolled Date</div>
                <p>{data?.enrolledDate ? dateTimeToCustomFormat(data?.campaignStart?.toString() ?? '') : '-'}</p>
              </div>
            </div>
            <div className={styles['details-info']}>
              <div className={styles['details-info-item']}>
                <div className={styles['label']}>Pin</div>
                <p>{data?.pin ?? '-'}</p>
              </div>
              <div className={styles['details-info-item']}>
                <div className={styles['label']}>Phone Number</div>
                <p>{data?.phone ?? '-'}</p>
              </div>
              <div className={styles['details-info-item']}>
                <div className={styles['label']}>Email</div>
                <p>{data?.email ?? '-'}</p>
              </div>
            </div>
            <div className={styles['details-info']}>
              <div className={styles['details-info-item']}>
                <div className={styles['label']}>Total Coach</div>
                <p>{data?.totalCoach ?? 0}</p>
              </div>
              <div className={styles['details-info-item']}>
                <div className={styles['label']}>Remarks</div>
                <p>{data?.remarks ?? '-'}</p>
              </div>
            </div>
          </div>
        </Card>
        <div className={styles['layout']}>
          <Card
            title={`Achievement Summary`}
            headerStyle={HeaderStyle.HEADER_5}
            //buttonType={ButtonType.ICON}
            //button={getButtonConfig.pointAllocation}
          >
            <div className={styles['allocation']}>
              <div className={styles['allocation-container']}>
                <div className={styles['allocation-container-item']}>
                  {/* <div className={styles['allocation-total']}>
                    <div className={styles['allocation-total-title']}>Qualified Coaches</div>
                    <div className={styles['allocation-total-number']}>{data?.totalQualified ?? 0}</div>
                  </div> */}
                  <div className={styles['allocation-total']}>
                    <div className={styles['allocation-total-title']}>Total Participants</div>
                    <div className={styles['allocation-total-number']}>{data?.summary?.completedCount ?? 0}</div>
                  </div>
                  {/* <div className={styles['allocation-total']}>
                    <div className={styles['allocation-total-title']}>Subscribed to SOP</div>
                    <div className={styles['allocation-total-number']}>{data?.summary?.alpCount ?? 0}</div>
                    <span>Note: This does not contribute to the coach/leader winning criteria.</span>
                  </div> */}
                  <div className={styles['allocation-total']}>
                    <div className={styles['allocation-total-title']}>Achieved Winning Criteria</div>
                    <div className={styles['allocation-total-number']}>{data?.summary?.achievedCount ?? 0}</div>
                  </div>
                  <div className={styles['allocation-total']}>
                    <div className={styles['allocation-total-title']}>New Personal Sponsored ABO/APC</div>
                    <div className={styles['allocation-total-number']}>{data?.summary?.newAboCount ?? 0}</div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
          <Card
            title={t('admin.participant.rewardTitle')}
            headerStyle={HeaderStyle.HEADER_5}
            buttonType={ButtonType.ICON}
            button={getButtonConfig.rewardGift}
          >
            <TableContainer
              dataSource={
                data?.rewards?.filter(
                  item => item?.rewardStatus && !['REMOVED', 'REMOVED_BY_ADJUSTMENT'].includes(item?.rewardStatus),
                ) ?? []
              }
              getColumns={getRewardListConfig}
              pagination={false}
              rowKey="rewardRecId"
            />
          </Card>
        </div>
        <Card title={`Coach`} headerStyle={HeaderStyle.HEADER_5} buttonType={ButtonType.ICON}>
          <TableContainer
            dataSource={data?.coaches ?? []}
            getColumns={getCoachListConfig}
            pagination={false}
            rowKey="recId"
            cb={{ onEdit }}
          />
        </Card>
      </div>
      {showModal}
      <AuditHistoryAccordion auditHistoryLog={auditHistoryLog} displayAuditEmpty={displayAuditEmpty} />
    </div>
  );
};
